import { DateRange, SerializedDateRange } from "JS/types/Generic";
import { ICheckoutPayment, OrderDiscountType, PromoBenefitType } from "JS/types/Checkout";
import { IconName } from "JS/constants/icons";
import { ILocalStoreJson } from "JS/types/LocalStore";
import { IProductPrice } from "../../../types/Product";
import { ISavedVehicle } from "JS/types/Automotive";
import { IThresholdPromoOffer } from "JS/types/Promo";

export interface IWorkOrderSessionResource {
	type: WorkOrderType;
	redirect: WorkOrderRedirect;
	setStep: AutomotiveSchedulerStep | null;
	maximumStep: AutomotiveSchedulerStep;
	primaryProductInfo: IWorkOrderInstallableProductResource | null;
	workOrderSummary: IWorkOrderSummaryResource;
	customerInfo: IWorkOrderCustomerInfoResource;
	serviceInfo: IWorkOrderServiceInfoResource;
	appointmentInfo: WorkOrderAppointmentInfo;
	paymentInfo: IWorkOrderPaymentInfoResource;
	error: WorkOrderError | null;
}

export enum WorkOrderType {
	Tire,
	Battery,
	ServiceOnly
}

export interface IWorkOrderProductResource {
	blainNumber: string;
	vendorBrand: string;
	productName: string;
	imageUrl: string;
	specifications: Record<string, string>;

}

export enum AutomotiveSchedulerStep {
	ContactInfo,
	Services,
	Appointment,
	Payment,
	Review,
}

export interface IWorkOrderSummaryResource {
	estimatedSubtotalString: string;
	earnableRewardsPoints: string | null;
	primaryProductLineItem: IWorkOrderSummaryLineItemResource;
	auxCharges: IWorkOrderSummaryLineItemResource[];
	product?: IWorkOrderProductResource;
	appliedDiscounts: IWorkOrderDiscountResource[] | null;
	thresholdPromos: IThresholdPromoOffer[];
	savings: number;
	savingsString: string;
	lineItems: IWorkOrderSummaryLineItemResource[];
	vehicleInfo: ISavedVehicle;
	rewardsMember: boolean;
	totalIsNotFinal: boolean;
	disclaimer: string | null;
	tax: string;
	grandTotal: string;
	totalSavings: string;
	total: WorkOrderTotalResource;
}

export interface IWorkOrderDiscountResource {
	id: number;
	orderID: number;
	refCode: string;
	description: string;
	amount: number;
	amountString: string;
	posLineNum: number;
	offerID: number | null;
	rewardsOfferID: string | null;
	discountType: OrderDiscountType;
	benefitTypeID: PromoBenefitType;
	amountHidden: boolean;
}

export interface IWorkOrderSummaryLineItemResource {
	name: string;
	description: string;
	price: string;
	priceIsFinal: boolean;
}

export interface IWorkOrderInstallableProductResource {
	name: string;
	description: string;
	imageUrl: string;
	imageUrlWebp: string;
	vendorBrand: string;
	selectedAttributes: IAttributeValue[];
	totalSavings: number;
	quantity: number;
	price: IProductPrice;
	isTire: boolean;
}

export interface IAttributeValue {
	attributeDescription: string;
	attributeValue: string;
}

export interface IWorkOrderCustomerInfoResource {
	firstName: string;
	lastName: string;
	emailAddress: string;
	phoneNumber: string;
	isGuest: boolean;
	hasApptTextReminders: boolean;
	hasPromoTextReminders: boolean;
	errors: IWorkOrderCustomerInfoValidationErrors;
}

export interface IWorkOrderCustomerInfoValidationErrors {
	firstName: string;
	lastName: string;
	emailAddress: string;
	phoneNumber: string;
}

export interface IWorkOrderServiceInfoResource {
	additionalServices: AdditionalServiceResource[];
	additionalComments: string;
	errors: IWorkOrderServiceInfoValidationErrors;
	additionalServicesRequired: boolean;
}

export interface IWorkOrderServiceInfoValidationErrors {
	missingRequiredService: boolean;
	additionalCommentsError: string;
	missingVehicle: boolean;
}

export enum AdditionalServiceResourceType {
	Single,
	Multiple,
}

interface IAdditionalServiceDetails {
	blainNumber: string;
	name: string;
	icon: IconName;
	description: string;
	price: {
		dollars: string;
		cents: string;
	};
	priceIsFinal: boolean;
	disclaimer: string | null;
}

export interface IAdditionalServiceSingleResource extends IAdditionalServiceDetails {
	type: AdditionalServiceResourceType.Single;
	blainNumber: string;
	isSelected: boolean;
}

export interface IAdditionalServiceWithOptionsResource extends IAdditionalServiceDetails {
	type: AdditionalServiceResourceType.Multiple;
	selectedOptionBlainNumber: string;
	options: IAdditionalServiceOptionResource[];
	isSelected: boolean;
}

export interface IAdditionalServiceOptionResource extends IAdditionalServiceDetails {
	blainNumber: string;
}

export type AdditionalServiceResource = IAdditionalServiceSingleResource | IAdditionalServiceWithOptionsResource;

export interface ISelectedService {
	icon: IconName,
	name: string;
}


export enum WorkOrderAppointmentInfoState {
	Empty,
	Dropoff,
	TimeSlot,
}

interface IWorkOrderAppointmentInfoBase {
	state: WorkOrderAppointmentInfoState;
	serviceCenter: ILocalStoreJson;
	estimatedAppointmentDuration: number | null;
	estimatedAppointmentDurationLabel: string;
}

export interface IWorkOrderEmptyAppointmentInfo extends IWorkOrderAppointmentInfoBase {
	state: WorkOrderAppointmentInfoState.Empty;
}

export interface IWorkOrderDropoffAppointmentInfo extends IWorkOrderAppointmentInfoBase {
	state: WorkOrderAppointmentInfoState.Dropoff;
	selectedDate: string | null;
}

export interface IWorkOrderTimeSlotAppointmentInfo extends IWorkOrderAppointmentInfoBase {
	state: WorkOrderAppointmentInfoState.TimeSlot;
	selectedTimeSlot: SerializedDateRange;
}

export type WorkOrderAppointmentInfo = IWorkOrderEmptyAppointmentInfo | IWorkOrderDropoffAppointmentInfo | IWorkOrderTimeSlotAppointmentInfo;

export interface IDateAndTimeStepTimeSlotRequest {
	timeSlot: DateRange;
}

export interface IDateAndTimeStepDropoffRequest {
	date: Date;
}

export interface IContactInfoUpdateRequest {
	firstName: string;
	lastName: string;
	emailAddress: string;
	phoneNumber: string;
	hasApptTextReminders: boolean;
	hasPromoTextReminders: boolean;
}

export interface IServiceInfoUpdateRequest {
	additionalServiceBlainNumbers: string[];
	additionalComments: string;
}

export interface IAppointmentInfoDropoffUpdateRequest {
	submissionType: 'dropoff';
	dropoffDate: Date;
}

export interface IAppointmentInfoTimeSlotUpdateRequest {
	submissionType: 'time-slot';
	timeSlot: DateRange;
}

export interface IAppointmentInfoIncompleteUpdateRequest {
	submissionType: 'incomplete';
}

export type IAppointmentInfoUpdateRequest =
	| IAppointmentInfoDropoffUpdateRequest
	| IAppointmentInfoTimeSlotUpdateRequest
	| IAppointmentInfoIncompleteUpdateRequest

export interface IWorkOrderPaymentInfoResource {
	prePay: boolean;
	paymentMethods: ICheckoutPayment[];
	hasRemainingBalance: boolean;
	errors: IWorkOrderPaymentInfoValidationErrors;
	paymentFlow: WorkOrderPaymentFlow;
	possibleTotals: AvailableTotals;
}

export type AvailableTotals = {
	[PrepaymentOption.None]?: WorkOrderTotalResource;
	[PrepaymentOption.InFull]?: WorkOrderTotalResource;
}

export enum WorkOrderPaymentFlow {
	PrePayOptional = 0,
	PrePayUnavailable = 1,
}

export interface IWorkOrderPaymentInfoValidationErrors {
	insufficientPayment: boolean;
	hasErrors: boolean;
}

export interface IApplyGiftCardRequest {
	giftCardNum: string;
	giftCardPin: string;
	isPayment: boolean;
}

export enum WorkOrderRedirectType {
	None,
	Expired,
	Completed,
}

export type WorkOrderRedirect =
	| INoopWorkOrderRedirect
	| IExpiredWorkOrderRedirect
	| ICompletedWorkOrderRedirect;

export interface INoopWorkOrderRedirect {
	type: WorkOrderRedirectType.None;
}

export interface IExpiredWorkOrderRedirect {
	type: WorkOrderRedirectType.Expired;
}

export interface ICompletedWorkOrderRedirect {
	type: WorkOrderRedirectType.Completed;
	orderId: number;
	orderHash: string;
}

export enum WorkOrderErrorType {
	Global,
	ReselectAppointment,
	PaymentError
}

export type WorkOrderError = GlobalWorkOrderError | ReselectAppointmentWorkOrderError | PaymentWorkOrderError;

export interface GlobalWorkOrderError {
	type: WorkOrderErrorType.Global;
	hideRestartOption: boolean;
	message: string;
}

export interface ReselectAppointmentWorkOrderError {
	type: WorkOrderErrorType.ReselectAppointment;
	message: string;
}

export interface PaymentWorkOrderError {
	type: WorkOrderErrorType.PaymentError;
	message: string;
}

export enum PrepaymentOption {
	InFull = 1,
	//Deposit = 2, //Need to keep value to stay in synch with Blain, but no longer used
	None = 3,
}

export enum WorkOrderTotalType {
	TBD = 0,
	Normal = 1,
}

export type WorkOrderTotalResource = IWorkOrderTBDTotal | IWorkOrderTotal;

export interface IWorkOrderTBDTotal {
	type: WorkOrderTotalType.TBD;
	prepaymentOption: PrepaymentOption;
}

export interface IWorkOrderTotal {
	type: WorkOrderTotalType.Normal;
	prepaymentOption: PrepaymentOption;
	amount: string;
	showAsEstimate: boolean;
}
